// ResultsPage.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import html2pdf from "html2pdf.js";
import stringSimilarity from "string-similarity";
import UpdateUserModal from "../components/UpdateUserModal";
import ResetPasswordModal from "../components/ResetPasswordModal";
import Header from "./Header";
import Footer from "./Footer";

// PDF Viewer imports
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

function ResultsPage() {
  const { documentIds } = useParams();
  const ids = useMemo(() => documentIds?.split(",") || [], [documentIds]);
  const [results, setResults] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const suggestionContentRef = React.useRef(null);
  const [suggestionsHeight, setSuggestionsHeight] = useState(400);
  const [userName, setUserName] = useState("");
  const [activeTab, setActiveTab] = useState("results");
  const [text, setText] = useState("");
  const [analysis, setAnalysis] = useState([]);
  const [filters, setFilters] = useState({ zelo: true, verjetno: true, malo: true });
  const [suggestions, setSuggestions] = useState("");
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const navigate = useNavigate();
  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const normalize = (str) => str.toLowerCase().replace(/\s+/g, " ").trim();

  const getColor = (confidence) => {
    if (confidence === "zelo verjetno") return "#86efac";
    if (confidence === "verjetno") return "#fdba74";
    return "#fca5a5";
  };
  const LOCAL_STORAGE_SUGGESTIONS_KEY = "suggestions_cache";

const loadCachedSuggestions = () => {
  const raw = localStorage.getItem(LOCAL_STORAGE_SUGGESTIONS_KEY);
  return raw ? JSON.parse(raw) : {};
};

const saveSuggestionsToCache = (map) => {
  localStorage.setItem(LOCAL_STORAGE_SUGGESTIONS_KEY, JSON.stringify(map));
};



const handleExportSuggestionsPDF = () => {
  const element = suggestionContentRef.current;
  if (!element) return;

  const opt = {
    margin:       0.5,
    filename:     'predlogi_dokumentov.pdf',
    image:        { type: 'jpeg', quality: 0.98 },
    html2canvas:  { scale: 2 },
    jsPDF:        { unit: 'in', format: 'a4', orientation: 'portrait' }
  };

  html2pdf().from(element).set(opt).save();
};


  useEffect(() => {
    const fetchUserInfo = async () => {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      try {
        const res = await axios.get("https://fnm-backend.aibc-robotics.com/me", {
          headers: { "X-User-ID": userId },
        });
        const { ime, priimek } = res.data;
        setUserName(`${ime} ${priimek}`);
      } catch (err) {
        console.error("Napaka pri pridobivanju uporabnika:", err);
      }
    };
    fetchUserInfo();
  }, []);

  const applyHighlights = useCallback((text, analysis, filters) => {
    let lines = text.split("\n");
    const applied = [...lines];
    analysis.forEach(({ odlomek, verjetnost }) => {
      let bestMatch = { index: -1, score: 0 };
      lines.forEach((line, i) => {
        const score = stringSimilarity.compareTwoStrings(normalize(line), normalize(odlomek));
        if (score > bestMatch.score) {
          bestMatch = { index: i, score };
        }
      });
      if (bestMatch.score >= 0.5 && bestMatch.index !== -1) {
        if ((verjetnost === "zelo verjetno" && filters.zelo) ||
            (verjetnost === "verjetno" && filters.verjetno) ||
            (verjetnost === "malo verjetno" && filters.malo)) {
          const color = getColor(verjetnost);
          const original = lines[bestMatch.index];
          const span = `<span style="background-color: ${color}; padding: 0.2em; border-radius: 0.25rem;">${original}</span>`;
          applied[bestMatch.index] = span;
        }
      }
    });
    return applied.join("\n");
  }, [filters]);

  useEffect(() => {
    const updated = results.map((res) => {
      const highlighted = applyHighlights(res.text, res.analysis, filters);
      return { ...res, highlighted };
    });
    setResults(updated);
  }, [filters, applyHighlights]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const responses = await Promise.all(
          ids.map((id) => axios.get(`https://fnm-backend.aibc-robotics.com/results/${id}`))
        );
        const processed = responses.map((res) => {
          const text = res.data.text;
          const analysis = typeof res.data.analysis === "string" ? JSON.parse(res.data.analysis) : res.data.analysis;
          const highlighted = applyHighlights(text, analysis, filters);
          return {
            id: res.data.document_id,
            text,
            analysis,
            highlighted,
            filename: res.data.filename,
          };
        });
        setResults(processed);
      } catch (err) {
        console.error("Napaka pri pridobivanju rezultatov:", err);
      }
    };
    fetchAll();
  }, [ids, filters]);

  useEffect(() => {
    if (!loadingSuggestions && suggestionContentRef.current) {
      setSuggestionsHeight(suggestionContentRef.current.offsetHeight);
    }
  }, [loadingSuggestions]);

useEffect(() => {
  if (activeTab !== "suggestions") return;

  const SUGGESTIONS_KEY = "suggestions_cache";

  const loadCache = () => {
    const raw = localStorage.getItem(SUGGESTIONS_KEY);
    return raw ? JSON.parse(raw) : {};
  };

  const saveCache = (data) => {
    localStorage.setItem(SUGGESTIONS_KEY, JSON.stringify(data));
  };

  const cached = loadCache();
  const missingIds = ids.filter((id) => !cached[id]);

  // Če je vse v cache-u:
  if (missingIds.length === 0) {
    setSuggestions(cached);
    setLoadingSuggestions(false);
    return;
  }

  // Sicer stream-aj manjkajoče:
  setLoadingSuggestions(true);
  const updated = { ...cached };
  let completed = 0;

  missingIds.forEach((id) => {
    const eventSource = new EventSource(`https://fnm-backend.aibc-robotics.com/suggestions-stream?document_id=${id}`);

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.complete) {
        updated[id] = JSON.stringify(data.suggestions, null, 2); // ✅ JSON lepo formatiran
        completed++;

        if (completed === missingIds.length) {
          setSuggestions(updated);
          setLoadingSuggestions(false);
          saveCache(updated); // ✅ Shrani v localStorage
        }
        eventSource.close();
      }
    };

    eventSource.onerror = (err) => {
      console.error("❌ Napaka pri streamanju:", err);
      eventSource.close();
      completed++;

      if (completed === missingIds.length) {
        setSuggestions(updated);
        setLoadingSuggestions(false);
        saveCache(updated); // ✅ Tudi ob napaki shrani (če karkoli prišlo)
      }
    };
  });
}, [activeTab, ids]);




  const toggleFilter = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 pt-24">
      <Header
        isLoggedIn={true}
        userName={userName}
        onLogin={() => {}}
        onLogout={() => { localStorage.clear(); navigate("/"); }}
        onUpdate={() => setShowUpdateModal(true)}
        onReset={() => setShowResetModal(true)}
        showBackButton={true}
        onBack={() => navigate("/")}
      />



      <div className="absolute top-6 right-6 space-x-2">
        <button onClick={() => navigate("/")} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100">← Nazaj na nalaganje</button>
        <button onClick={() => { localStorage.clear(); navigate("/"); }} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Odjava</button>
      </div>

      <div className="w-full max-w-7xl mx-auto bg-white rounded-xl shadow-lg p-8 min-h-[500px]">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Rezultati analize z dokumenti DigComp 2.2 in DigCompEdu</h1>

        <div className="flex border-b mb-4 space-x-6">
          <button onClick={() => setActiveTab("results")} className={`pb-2 border-b-2 ${activeTab === "results" ? "border-blue-600 text-blue-600 font-semibold" : "border-transparent text-gray-600"}`}>Rezultati analize</button>
          <button onClick={() => setActiveTab("suggestions")} className={`pb-2 border-b-2 ${activeTab === "suggestions" ? "border-blue-600 text-blue-600 font-semibold" : "border-transparent text-gray-600"}`}>Predlogi za izboljšave</button>
        </div>

        {activeTab === "results" && (
          <>
            <div className="space-x-4 mb-4">
              <label><input type="checkbox" checked={filters.zelo} onChange={() => toggleFilter("zelo")} /> Zelo verjetno</label>
              <label><input type="checkbox" checked={filters.verjetno} onChange={() => toggleFilter("verjetno")} /> Verjetno</label>
              <label><input type="checkbox" checked={filters.malo} onChange={() => toggleFilter("malo")} /> Malo verjetno</label>
            </div>

            <div className="grid gap-6 grid-cols-1">
              {results.map((res, i) => {
                const pdfPath = `https://fnm-backend.aibc-robotics.com/static/${res.filename}`;


                return (
                  <div key={res.id} className="border rounded-md bg-gray-50 p-4 shadow-md">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">📄 Dokument {i + 1}</h3>
                      <button onClick={() => {
                        const element = document.getElementById(`result-content-${i}`);
                        html2pdf().set({ margin: 1, filename: `dokument_${i + 1}.pdf`, html2canvas: { scale: 2 }, jsPDF: { unit: "in", format: "a4", orientation: "portrait" } }).from(element).save();
                      }} className="px-4 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">Izvozi ta dokument v PDF</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div id={`result-content-${i}`} className="whitespace-pre-wrap bg-white border p-4 rounded-md" dangerouslySetInnerHTML={{ __html: res.highlighted }} />
                      <div className="overflow-auto border rounded-md p-2 bg-gray-100 max-h-[800px]">
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                          <Viewer fileUrl={pdfPath} plugins={[defaultLayoutPluginInstance]} />
                        </Worker>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {activeTab === "suggestions" && (
          <div className="w-full flex justify-center">
            <div className="w-full max-w-5xl bg-white p-6 rounded-lg border" style={{ minHeight: suggestionsHeight }}>
              {loadingSuggestions ? (
                <div className="flex items-center justify-center h-full text-blue-600 font-medium">
                  <svg className="animate-spin h-5 w-5 mr-2 text-blue-600" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  <span>Nalaganje predlogov...</span>
                </div>
              ) : (
                <div ref={suggestionContentRef}>
                  <div className="flex justify-end mb-4">
                  <button
                    onClick={handleExportSuggestionsPDF}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                  >
                    Izvozi predloge v PDF
                  </button>

                  </div>

                    {Object.entries(suggestions).map(([id, text], index) => (
                      <div key={id} className="mb-6">
                        <h3 className="font-semibold mb-2">📘 Predlogi za dokument {index + 1}</h3>
                        <div className="bg-blue-50 p-4 border rounded-md">
                          {Array.isArray(JSON.parse(text)) ? (
                            JSON.parse(text).map((item, idx) => (
                              <div key={idx} className="mb-4">
                                <p><strong>Kompetenca:</strong> {item.kompetenca}</p>
                                <p><strong>Povzetek:</strong> {item.povzetek}</p>
                                <p><strong>Obrazložitev:</strong> {item.obrazložitev}</p>
                                <hr className="my-2" />
                              </div>
                            ))
                          ) : (
                            <pre className="whitespace-pre-wrap">{text}</pre>
                          )}
                        </div>
                      </div>
                    ))}

                </div>
              )}
            </div>
          </div>
        )}

        {showUpdateModal && <UpdateUserModal onClose={() => setShowUpdateModal(false)} onUpdated={(newName) => setUserName(newName)} />}
        {showResetModal && <ResetPasswordModal onClose={() => setShowResetModal(false)} />}
      </div>

      <Footer />
    </div>
  );
}

export default ResultsPage;
